/* Customizations for Rebuy Smart Search: B2B/Market pricing, quick view UI sync, filter pill behavior, breadcrumb title mirror */

const fetchProductData = async (handle) => {
  const response = await fetch(`${Shopify.routes.root}products/${handle}.js`);
  return response.json();
};

const simulatedPriceMapPromise = (async () => {
  try {
    const response = await fetch(`${Shopify.routes.root}?section_id=simulated-prices-map`);
    const text = await response.text();
    const doc = new DOMParser().parseFromString(text, 'text/html');
    const json = doc.querySelector('[data-simulated-prices]')?.textContent;
    const entries = json ? Object.entries(JSON.parse(json)) : [];
    return new Map(entries.map(([handle, cents]) => [handle, Number(cents)]));
  } catch (error) {
    return new Map();
  }
})();

const displayPrices = (price, compareAtPrice, simulatedCents) => {
  if (!simulatedCents) return { price, compareAtPrice };
  const crossedOut = compareAtPrice && compareAtPrice > price ? compareAtPrice : price;
  return { price: simulatedCents, compareAtPrice: crossedOut };
};

const applySimulatedPrices = async (products) => {
  const map = await simulatedPriceMapPromise;
  (products ?? []).forEach((product) => {
    const simulatedCents = map.get(product.handle);
    if (!simulatedCents) return;
    const items = [product, product.selected_variant, ...(product.variants ?? [])];
    items.forEach((item) => {
      if (!item || !Number(item.price)) return;
      const prices = displayPrices(Number(item.price), Number(item.compareAtPrice) || 0, simulatedCents);
      item.price = prices.price;
      item.compareAtPrice = prices.compareAtPrice;
    });
  });
};

const matchesProduct = (product, data) =>
  Number(product.id) === data.id || product.productId === data.id;

const updateVariantPrices = (product, data, simulatedCents) => {
  if (!product.variants) {
    if (product.selected_variant) {
      const prices = displayPrices(data.price, data.compare_at_price, simulatedCents);
      product.selected_variant.price = prices.price;
      product.selected_variant.compareAtPrice = prices.compareAtPrice;
    }
    return;
  }

  const updatedVariantsById = new Map(
    (data.variants ?? []).map((variant) => [variant.id, variant])
  );

  product.variants.forEach((variant) => {
    const updated = updatedVariantsById.get(Number(variant.id));
    if (!updated) return;
    const prices = displayPrices(updated.price, updated.compare_at_price, simulatedCents);
    variant.price = prices.price;
    variant.compareAtPrice = prices.compareAtPrice;
  });
};

const updateSearchPrices = (products, type) => {
  setTimeout(() => {
    products.forEach(async (product) => {
      try {
        const [data, simulatedPriceMap] = await Promise.all([
          fetchProductData(product.handle),
          simulatedPriceMapPromise,
        ]);
        if (!matchesProduct(product, data)) return;

        const simulatedCents = simulatedPriceMap.get(product.handle) ?? 0;
        const prices = displayPrices(data.price, data.compare_at_price, simulatedCents);
        product.price = prices.price;
        product.compareAtPrice = prices.compareAtPrice;
        updateVariantPrices(product, data, simulatedCents);
      } catch (error) {
        console.error(`[rebuy-pricing] failed to update ${type}`, error);
      }
    });
  }, 500);
};

/* quick view loads, update suggested products */
document.addEventListener('rebuy:smartsearch.quickview.ready', (event) => {
  applySimulatedPrices(event.detail.smartSearchQuickView.setting.suggestedProducts);
  setTimeout(() => {
    updateSearchPrices(
      event.detail.smartSearchQuickView.setting.suggestedProducts,
      'QUICKVIEW ready'
    );
  }, 500);
});

/* quickview search products returned */
document.addEventListener('rebuy:smartsearch.quickview.productsChange', (event) => {
  applySimulatedPrices(event.detail.products);
  updateSearchPrices(event.detail.products, 'QUICKVIEW change');
});

/* Hide accessiBe trigger while the smart search quick view is open */
(() => {
  const quickViewOpenClass = 'rebuy-smart-search-quickview-open';
  const mobileMedia = window.matchMedia('(max-width: 768px)');
  let quickViewIsOpen = false;

  const syncQuickViewClass = () => {
    document.body.classList.toggle(quickViewOpenClass, quickViewIsOpen && mobileMedia.matches);
  };

  const syncEmbeddedVisibility = () => {
    const embedded = document.getElementById('rebuy-quick-view-embedded');
    if (embedded) embedded.classList.toggle('rebuy-quick-view-visible', quickViewIsOpen);
  };

  document.addEventListener('rebuy:smartsearch.quickview.open', () => {
    quickViewIsOpen = true;
    syncQuickViewClass();
    syncEmbeddedVisibility();
  });

  document.addEventListener('rebuy:smartsearch.quickview.close', () => {
    quickViewIsOpen = false;
    syncQuickViewClass();
    syncEmbeddedVisibility();
  });

  if (typeof mobileMedia.addEventListener === 'function') {
    mobileMedia.addEventListener('change', syncQuickViewClass);
  } else {
    mobileMedia.addListener(syncQuickViewClass);
  }
})();

/* results page search products returned */
document.addEventListener('rebuy:smartsearch.resultsPage.productsChange', (event) => {
  applySimulatedPrices(event.detail.products);
  setTimeout(() => {
    updateSearchPrices(event.detail.products, 'RESULTSPAGE');
  }, 500);
});

/*
 * Search page: single source of truth for the live result count (products + content).
 * Shopify's native count only includes products; Rebuy renders the true total client-side,
 * so we read it from the DOM and keep both the breadcrumb and the browser tab title in sync,
 * holding them against Rebuy/Shopify re-renders.
 */
(() => {
  const root = document.getElementById('rebuy-search-results-page');
  if (!root) return;

  const breadcrumb = document.querySelector('.rebuy-search-breadcrumbs .breadcrumbs__text');

  // Seed query/prefix from the server-rendered breadcrumb, e.g. `Search results "produc"`.
  const initialText = breadcrumb ? breadcrumb.textContent.trim() : '';
  const prefix = initialText.replace(/\s*"[^"]*"\s*$/, '').trim() || 'Search results';
  const initialTerm = (initialText.match(/"([^"]*)"/) || [])[1] || '';

  const shopName = document.querySelector('meta[property="og:site_name"]')?.content || '';

  const isVisible = (el) => !!(el && el.offsetParent !== null);

  const firstInt = (text) => {
    const match = (text || '').match(/\d[\d,]*/);
    return match ? parseInt(match[0].replace(/,/g, ''), 10) : null;
  };

  // Real total = products + content. Rebuy prints it in the result-title; otherwise sum the tab counts.
  const totalCount = () => {
    const summary = root.querySelector('.rebuy-smart-search-results-page-dropdown__result-title');
    if (summary) {
      const summaryCount = firstInt(summary.textContent);
      if (summaryCount !== null) return summaryCount;
    }
    const tabs = root.querySelectorAll('.rebuy-smart-search-results-page-dropdown__tab');
    if (tabs.length) {
      let total = 0;
      let found = false;
      tabs.forEach((tab) => {
        const nums = (tab.textContent || '').match(/\d[\d,]*/g);
        if (nums && nums.length) {
          total += parseInt(nums[nums.length - 1].replace(/,/g, ''), 10);
          found = true;
        }
      });
      if (found) return total;
    }
    if (isVisible(root.querySelector('.rebuy-smart-search-results-page-dropdown__no-results'))) {
      return 0;
    }
    return null;
  };

  const currentQuery = () => {
    const input = /** @type {HTMLInputElement | null} */ (
      root.querySelector('.rebuy-smart-search-results-page-dropdown__input')
    );
    // Once Rebuy has rendered the input, trust its value (empty means the user cleared it).
    // Only fall back to the server-seeded term before the input exists.
    return input ? input.value.trim() : initialTerm;
  };

  const updateBreadcrumb = (query, count) => {
    if (!breadcrumb) return;
    if (!query) {
      breadcrumb.textContent = prefix;
      return;
    }
    // count === null only transiently (before Rebuy renders) — fall back to the plain prefix.
    breadcrumb.textContent = count === null
      ? `${prefix} "${query}"`
      : `Showing ${count} results for "${query}"`;
  };

  const updateTitle = (query, count) => {
    let base;
    if (!query) {
      base = prefix;
    } else if (count === null) {
      base = `Search results for "${query}"`;
    } else {
      base = `Search: ${count} results found for "${query}"`;
    }
    const next = (shopName && base.indexOf(shopName) === -1) ? `${base} – ${shopName}` : base;
    if (document.title !== next) document.title = next;
  };

  const enforce = () => {
    const query = currentQuery();
    // No active query → ignore any stale count still in the DOM mid-rerender.
    const count = query ? totalCount() : null;
    updateBreadcrumb(query, count);
    updateTitle(query, count);
  };

  enforce();
  document.addEventListener('DOMContentLoaded', enforce);
  window.addEventListener('load', enforce);

  const observer = new MutationObserver(enforce);
  observer.observe(root, { childList: true, subtree: true, characterData: true });
  const titleEl = document.querySelector('title');
  if (titleEl) observer.observe(titleEl, { childList: true, characterData: true, subtree: true });
})();


(() => {
  const root = document.getElementById('rebuy-search-results-page');
  if (!root) return;

  const TYPING_TIMEOUT_MS = 700;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let typingTimer;

  root.addEventListener('input', (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (!target?.matches?.('.rebuy-smart-search-results-page-dropdown__input')) return;

    root.classList.add('rebuy-search--typing');
    clearTimeout(typingTimer);
    typingTimer = setTimeout(() => root.classList.remove('rebuy-search--typing'), TYPING_TIMEOUT_MS);
  });
})();

